// src/hooks/useSystemAnalytics.ts

import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import systemAnalyticsService from '../services/systemAnalytics.service';
import type {
  SystemOverviewData,
  WeeklyContributionsData,
  SolidarityContributionsData,
  SystemCreditsData,
  GroupActivitiesData,
  OtherIncomeData,
  SystemSavingsData,
  SystemAnalyticsFilters,
} from '../services/systemAnalytics.service';

// Hook for system overview data
export const useSystemOverview = (filters: SystemAnalyticsFilters = {}) => {
  const [data, setData] = useState<SystemOverviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await systemAnalyticsService.getCommunesOverview(filters);
      if (response.success) {
        setData(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch system overview');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch system overview';
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

// Hook for weekly contributions data
export const useSystemWeeklyContributions = (filters: SystemAnalyticsFilters = {}) => {
  const [data, setData] = useState<WeeklyContributionsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await systemAnalyticsService.getWeeklyContributionsSummary(filters);
      if (response.success) {
        setData(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch weekly contributions');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch weekly contributions';
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

// Hook for solidarity contributions data
export const useSystemSolidarityContributions = (filters: SystemAnalyticsFilters = {}) => {
  const [data, setData] = useState<SolidarityContributionsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await systemAnalyticsService.getSolidarityContributionsSummary(filters);
      if (response.success) {
        setData(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch solidarity contributions');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch solidarity contributions';
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

// Hook for system credits data
export const useSystemCredits = (filters: SystemAnalyticsFilters = {}) => {
  const [data, setData] = useState<SystemCreditsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await systemAnalyticsService.getCreditsSummary(filters);
      if (response.success) {
        setData(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch system credits');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch system credits';
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

// Hook for comprehensive system dashboard data
export const useSystemDashboard = (filters: SystemAnalyticsFilters = {}) => {
  const [data, setData] = useState<SystemOverviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const dashboardData = await systemAnalyticsService.getSystemDashboardData(filters);
      console.log('Hook received dashboard data:', dashboardData);
      setData(dashboardData);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch system dashboard data';
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportData = useCallback(async (format: 'csv' | 'excel') => {
    try {
      const blob = await systemAnalyticsService.exportSystemAnalytics(format, filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `system-analytics-${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success(`System analytics exported as ${format.toUpperCase()}`);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : `Failed to export ${format}`;
      message.error(errorMsg);
    }
  }, [filters]);

  return { 
    data, 
    loading, 
    error, 
    refetch: fetchData,
    exportData,
  };
};

